import type { Space } from "@melo/shared";
import { store, useTables } from "../lib/store";
import { byRank, formatDate, keyOf, statusOf, todayIso } from "../lib/select";
import { openItem } from "../lib/nav";
import { Empty } from "../components/primitives";
import { StatusChip } from "../components/pickers";

/** What a contractor sees in a project: the work assigned to them, most urgent first. */
export function ContractorWork({ space }: { space: Space }) {
  const t = useTables();
  const me = store.me!.id;
  const mine = [...t.nodes.values()].filter((n) => n.spaceId === space.id && !n.draft && n.assignees.some((a) => a.actorId === me));
  const cat = (n: (typeof mine)[number]) => statusOf(t.kinds.get(n.kindId), n.status)?.category ?? "todo";
  const groups: { title: string; items: typeof mine }[] = [
    { title: "Changes requested or in progress", items: mine.filter((n) => cat(n) === "active") },
    { title: "To do", items: mine.filter((n) => cat(n) === "todo") },
    { title: "Waiting for review", items: mine.filter((n) => cat(n) === "review") },
    { title: "Done", items: mine.filter((n) => cat(n) === "done") },
  ].filter((g) => g.items.length);
  const today = todayIso();
  return (
    <div className="pad" style={{ maxWidth: 900 }}>
      <div className="greet" style={{ marginBottom: 16 }}>
        <h1>Your work on {space.name}</h1>
        <p>You see the work assigned to you, with its brief and references.</p>
      </div>
      {groups.map((g) => (
        <section key={g.title} className="block" style={{ marginBottom: 16 }}>
          <div className="block-h">
            <h3>{g.title}</h3>
            <span className="muted">{g.items.length}</span>
          </div>
          {g.items.sort((a, b) => (a.dueDate ?? "9999").localeCompare(b.dueDate ?? "9999") || byRank(a, b)).map((n) => {
            const parent = n.parentId ? t.nodes.get(n.parentId) : undefined;
            return (
              <button key={n.id} type="button" className="row" onClick={() => openItem(keyOf(t, n))}>
                <span className="mono muted key">{keyOf(t, n)}</span>
                <span className="t">
                  <span>{n.title}</span>
                  <small>{parent ? parent.title : space.name}</small>
                </span>
                {n.dueDate && <span className={`due${n.dueDate < today && cat(n) !== "done" ? " late" : ""}`}>{formatDate(n.dueDate)}</span>}
                <StatusChip kind={t.kinds.get(n.kindId)} status={n.status} />
              </button>
            );
          })}
        </section>
      ))}
      {!groups.length && <Empty title="Nothing is assigned to you here yet." />}
    </div>
  );
}
